import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Home as HomeIcon, Dumbbell, Wifi, Check, BadgePercent, GraduationCap, Trophy } from "lucide-react";
import { useSeo } from "@/components/Layout";
import { RevealLines, Reveal, RevealHeading, Stagger, StaggerItem } from "@/components/motion-primitives";
import { CtaButton, SectionLabel, BrandMarquee } from "@/components/ui-blocks";
import { SITE, IMAGES } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const SERVICE_IMG = [IMAGES.floor, IMAGES.salleClient, IMAGES.prepa];
const SERVICE_ICON = [HomeIcon, Dumbbell, Wifi];

export default function Home() {
  const { t } = useLang();
  const h = t.home;
  useSeo(h.seoTitle, h.seoDesc);
  const showTax = Boolean(SITE.sapNumber);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden grain pt-32 lg:pt-32 pb-12 lg:pb-16">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <SectionLabel>{t.nav.home === "Home" ? "Personal trainer · Nice" : "Coach sportif · Nice"}</SectionLabel>
            </motion.div>
            <RevealLines as="div" data-testid="hero-accroche" delay={0.2}
              className="display-hero text-ink mt-5" lines={h.heroLines} />
            <Reveal delay={0.55}>
              <h1 className="mt-5 font-display text-base sm:text-lg font-bold uppercase tracking-tight text-ink/80">{h.seoH1}</h1>
            </Reveal>
            <Reveal delay={0.65} className="mt-4 max-w-xl">
              <p className="text-muted-foreground leading-relaxed">{h.heroSub}</p>
            </Reveal>
            <Reveal delay={0.75} className="mt-5 flex flex-wrap gap-2">
              {h.proofs.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-2 text-xs font-mono-alt uppercase tracking-wider text-ink border border-border rounded-full px-3 py-1.5 bg-card">
                  <span className="w-1.5 h-1.5 rounded-full bg-accentbrand" />{p}
                </span>
              ))}
            </Reveal>
            <Reveal delay={0.85} className="mt-7 flex flex-col sm:flex-row gap-3">
              <CtaButton to="/contact" data-testid="hero-cta-primary" className="w-full sm:w-auto justify-center">{t.common.bilanOffert}</CtaButton>
              <CtaButton to="/coaching-a-domicile" variant="outline" data-testid="hero-cta-secondary" className="w-full sm:w-auto justify-center">{t.common.decouvrir}</CtaButton>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] overflow-hidden bg-secondary max-w-md mx-auto lg:max-w-none">
              <motion.img src={IMAGES.portrait} alt="Gihed Kebir, coach sportif à Nice"
                style={{ y: imgY }} className="w-full h-full object-cover object-center will-change-transform" />
            </div>
            <div className="absolute left-3 bottom-3 bg-night text-white px-4 py-3 hidden sm:block">
              <p className="font-mono-alt text-[10px] text-white/60 uppercase tracking-wider">{h.badgeTop}</p>
              <p className="font-display font-bold text-base">{h.badgeMain}</p>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* COACH PRESENTATION — 2 columns */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] overflow-hidden bg-secondary max-w-md mx-auto lg:max-w-none">
              <img src={IMAGES.floor} alt="Gihed Kebir en séance de coaching" loading="lazy" className="w-full h-full object-cover object-center" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionLabel>{h.introLabel}</SectionLabel>
            <RevealHeading as="h2" text={h.introHeading} className="mt-1 display-2 text-ink" />
            <Reveal delay={0.1}>
              <div className="mt-2 space-y-3 text-muted-foreground leading-relaxed">
                {h.introText.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </Reveal>
            <div className="mt-6 grid sm:grid-cols-2 gap-6 sm:gap-8">
              <div className="border-t-2 border-ink pt-4">
                <div className="flex items-center gap-2 text-accentbrand mb-3">
                  <GraduationCap size={16} />
                  <p className="font-mono-alt text-[11px] uppercase tracking-wider">{t.about.diplomasHeading}</p>
                </div>
                {t.about.diplomas.map((q, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-1.5">
                    <Check size={15} className="text-accentbrand mt-0.5 shrink-0" /><span className="text-sm text-ink">{q}</span>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-ink pt-4">
                <div className="flex items-center gap-2 text-accentbrand mb-3">
                  <Trophy size={16} />
                  <p className="font-mono-alt text-[11px] uppercase tracking-wider">{t.about.expHeading}</p>
                </div>
                {t.about.experience.map((q, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-1.5">
                    <Check size={15} className="text-accentbrand mt-0.5 shrink-0" /><span className="text-sm text-ink">{q}</span>
                  </div>
                ))}
              </div>
            </div>
            <Reveal delay={0.1} className="mt-6"><CtaButton to="/a-propos" variant="outline" data-testid="intro-cta">{t.common.enSavoirPlus}</CtaButton></Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES — background photo cards */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
          <div className="max-w-2xl mb-4">
            <SectionLabel>{h.servicesLabel}</SectionLabel>
            <RevealHeading as="h2" text={h.servicesHeading} className="mt-1 display-1 text-ink" />
          </div>
          <Stagger className="grid md:grid-cols-3 gap-5" gap={0.08}>
            {h.services.map((s, i) => {
              const Icon = SERVICE_ICON[i];
              return (
                <StaggerItem key={i}>
                  <a href={s.to} data-testid={`service-card-${i}`}
                    className="group relative block overflow-hidden h-[520px] sm:h-[560px] bg-night">
                    <img src={SERVICE_IMG[i]} alt={s.title} loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-transparent" />
                    {s.hasTax && showTax && (
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-accentbrand text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1.5 z-10">
                        <BadgePercent size={13} /> {t.common.taxBadge}
                      </span>
                    )}
                    <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
                      <div className="flex items-center gap-2 text-accentbrand mb-2">
                        <Icon size={18} /><span className="font-mono-alt text-[11px] uppercase tracking-wider">{s.tag}</span>
                      </div>
                      <h3 className="font-display text-2xl font-bold uppercase tracking-tight">{s.title}</h3>
                      <p className="mt-2 text-sm text-white/80 leading-relaxed">{s.text}</p>
                      <ul className="mt-3 space-y-1 max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                        {s.advantages.map((a, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-white/90"><Check size={14} className="text-accentbrand mt-0.5 shrink-0" />{a}</li>
                        ))}
                      </ul>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white group-hover:text-accentbrand transition-colors">
                        {h.discover} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </a>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* METHOD */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
        <div className="max-w-2xl mb-4">
          <SectionLabel>{h.methodLabel}</SectionLabel>
          <RevealHeading as="h2" text={h.methodHeading} className="mt-1 display-1 text-ink" />
        </div>
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" gap={0.08}>
          {h.method.map((m, i) => (
            <StaggerItem key={i} className="border-t-2 border-ink pt-5">
              <span className="stroke-number font-display font-black text-5xl leading-none">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 font-display text-xl font-semibold text-ink">{m.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.text}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* SÉANCE BILAN */}
      <section className="bg-night text-white">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20 grid lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-accentbrand"><span className="w-6 h-px bg-accentbrand" />{h.bilanLabel}</span>
            <RevealHeading as="h2" text={h.bilanHeading} className="mt-1 display-1" />
            <Reveal delay={0.1}><p className="mt-2 text-white/70 leading-relaxed">{h.bilanNote}</p></Reveal>
          </div>
          <Stagger className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8" gap={0.06}>
            {h.bilanItems.map((it, i) => (
              <StaggerItem key={i} className="flex items-start gap-3 py-3 border-t border-white/15">
                <span className="font-mono-alt text-xs text-accentbrand pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/90">{it}</span>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
        <div className="mb-4">
          <SectionLabel>{h.testiLabel}</SectionLabel>
          <RevealHeading as="h2" text={h.testiHeading} className="mt-1 display-1 text-ink" />
          <Reveal delay={0.1}><p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{h.testiSubheading}</p></Reveal>
        </div>
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" gap={0.05}>
          {t.testimonials.map((item, i) => (
            <StaggerItem key={i} className="border border-border bg-card p-6 flex flex-col h-full">
              <div className="flex gap-1 text-accentbrand mb-4 text-sm">{Array.from({ length: 5 }).map((_, s) => <span key={s}>★</span>)}</div>
              <p className="text-ink leading-relaxed flex-1 text-sm">“{item.text}”</p>
              <div className="mt-5 pt-4 border-t border-border">
                <p className="font-display text-ink">
                  <span className="font-bold">{item.name}</span>
                  {item.age && <span className="font-medium text-ink/70">, {item.age} {t.nav.home === "Home" ? "yrs" : "ans"}</span>}
                </p>
                <p className="font-mono-alt text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5 break-words">{item.type} · {item.objective}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* OBJECTIVES */}
      <section className="bg-secondary/50">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-12 lg:py-20">
          <div className="max-w-2xl mb-4">
            <SectionLabel>{h.objLabel}</SectionLabel>
            <RevealHeading as="h2" text={h.objHeading} className="mt-1 display-1 text-ink" />
          </div>
          <Stagger className="grid grid-cols-2 sm:grid-cols-3 gap-3" gap={0.04}>
            {t.objectives.map((o, i) => (
              <StaggerItem key={i} className="bg-card border border-border p-5">
                <span className="font-mono-alt text-[11px] text-accentbrand">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 font-display text-lg font-semibold text-ink">{o}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
